// Pop-up notifications control for the messagerie (web).
//
// Lets a user turn browser pop-up notifications on/off for THIS device. "On"
// registers the device's FCM token (asking permission if needed) so the backend
// pushes new-message notifications, which the service worker shows as a system
// pop-up even when the user is working in another tab. "Off" removes the token.
//
// Exposes a hook (so the bell button and the discoverability prompt share one
// status) plus two presentational components.

import React, { useState, useEffect, useCallback } from "react";
import { NotificationManager } from "../config/FirebaseNotificationSystem";
import { Icon } from "./messagerieUi";

const PROMPT_DISMISS_KEY = "messagerie_notif_prompt_dismissed";

const isSupported = () =>
  typeof window !== "undefined" &&
  "Notification" in window &&
  "serviceWorker" in navigator &&
  "PushManager" in window;

// status: "loading" | "on" | "off" | "denied" | "unsupported"
export function useNotificationStatus(userId, prefix) {
  const [status, setStatus] = useState("loading");
  const [working, setWorking] = useState(false);

  const refresh = useCallback(async () => {
    if (!isSupported()) {
      setStatus("unsupported");
      return;
    }
    if (Notification.permission === "denied") {
      setStatus("denied");
      return;
    }
    if (!userId) return;
    try {
      const mgr = new NotificationManager();
      const on = await mgr.isEnabledForDevice(userId);
      setStatus(on ? "on" : "off");
    } catch {
      setStatus("off");
    }
  }, [userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const enable = useCallback(async () => {
    if (!userId || !prefix || working) return;
    setWorking(true);
    try {
      const mgr = new NotificationManager();
      // feedback=false: don't pop antd's "déjà activées" modal from the toggle.
      await mgr.initialize(userId, prefix, false);
      await refresh();
    } catch (e) {
      console.warn("[messagerie] enable notifications failed:", e?.message);
      if (typeof Notification !== "undefined" && Notification.permission === "denied") {
        setStatus("denied");
      }
    } finally {
      setWorking(false);
    }
  }, [userId, prefix, working, refresh]);

  const disable = useCallback(async () => {
    if (!userId || !prefix || working) return;
    setWorking(true);
    try {
      const mgr = new NotificationManager();
      await mgr.disableForDevice(userId, prefix);
      await refresh();
    } catch (e) {
      console.warn("[messagerie] disable notifications failed:", e?.message);
    } finally {
      setWorking(false);
    }
  }, [userId, prefix, working, refresh]);

  return { status, working, enable, disable };
}

// Bell toggle for the sidebar header.
export function NotificationsBell({ status, working, enable, disable }) {
  if (status === "loading" || status === "unsupported") return null;
  const on = status === "on";
  const denied = status === "denied";
  const title = denied
    ? "Notifications bloquées par le navigateur — autorisez-les dans les réglages du site"
    : on
    ? "Notifications pop-up activées — cliquer pour désactiver"
    : "Notifications pop-up désactivées — cliquer pour activer";
  return (
    <button
      onClick={() => {
        if (denied || working) return;
        if (on) disable();
        else enable();
      }}
      title={title}
      className="hover:bg-neutral-100 rounded-md flex items-center justify-center"
      style={{ width: 32, height: 32, opacity: working ? 0.5 : 1, cursor: denied ? "help" : "pointer" }}
    >
      <Icon name={on ? "bell" : "bell-off"} size={16} color={on ? "#6366f1" : "#A1A1A1"} />
    </button>
  );
}

// Discoverability banner shown when pop-ups are off (and not dismissed).
export function NotificationsPrompt({ status, working, enable }) {
  const [dismissed, setDismissed] = useState(
    () => typeof window !== "undefined" && localStorage.getItem(PROMPT_DISMISS_KEY) === "1"
  );
  if (dismissed) return null;
  if (status !== "off" && status !== "denied") return null;

  const denied = status === "denied";

  return (
    <div className="flex flex-row items-start" style={{ gap: 10, padding: "10px 12px", background: "#EEF0FF", borderRadius: 10 }}>
      <span style={{ marginTop: 1 }}>
        <Icon name="bell" size={16} color="#464DFF" />
      </span>
      <div className="flex flex-col" style={{ flex: 1, gap: 6 }}>
        <span style={{ fontFamily: "Poppins", fontSize: 12, lineHeight: "16px", color: "#3A3FBF" }}>
          {denied
            ? "Les notifications sont bloquées par votre navigateur. Autorisez-les dans les réglages du site pour recevoir un pop-up à chaque nouveau message, même sur un autre onglet."
            : "Activez les pop-up pour être prévenu des nouveaux messages, même quand vous êtes sur un autre onglet."}
        </span>
        {!denied && (
          <button
            onClick={enable}
            disabled={working}
            style={{ alignSelf: "flex-start", fontFamily: "Poppins", fontWeight: 600, fontSize: 12, color: "#FFFFFF", background: "#464DFF", padding: "6px 12px", borderRadius: 8, opacity: working ? 0.6 : 1 }}
          >
            {working ? "Activation…" : "Activer les notifications"}
          </button>
        )}
      </div>
      <button
        onClick={() => {
          setDismissed(true);
          try {
            localStorage.setItem(PROMPT_DISMISS_KEY, "1");
          } catch {
            /* ignore storage errors (private mode) */
          }
        }}
        title="Ne plus afficher"
        className="hover:bg-white rounded-md flex items-center justify-center flex-shrink-0"
        style={{ width: 22, height: 22 }}
      >
        <Icon name="close" size={10} color="#7C81E8" />
      </button>
    </div>
  );
}
